import React from 'react';

import { useLocalSearchParams } from 'expo-router';
import { MenuItemUnavailable } from 'features/menu/components/menu-item-unavailable';
import { MenuProductSkeleton } from 'features/menu/components/menu-product-skeleton';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { SHOULD_CHECK_ITEM_IN_OFFER } from 'features/menu/pages/main-menu-page/constants';
import { useHandleMenuItemInOffer } from 'features/menu/pages/menu-object-page/use-handle-menu-item-in-offer';
import { useLogMenuContent } from 'features/menu/pages/menu-object-page/use-log-menu-content';

import { MenuObject, SanityMenuObject } from '@rbi-ctg/menu';
import { CardModal } from 'components/card-modal';
import { PageAnnouncer } from 'components/page-announcer';
import { ProductDetail } from 'components/product-detail-v2';
import { MenuObjectTypes } from 'enums/menu';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { getStaticPathname } from 'navigation/location/location';
import { useMenuContext } from 'state/menu';
import { useGetMenuObjectQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-query';
import { ProductWizardProvider } from 'state/product-wizard-v2';
import { ProductMenuObject } from 'state/product-wizard-v2/types';

export const MenuProduct = () => {
  const pathname = getStaticPathname();
  const { id, menuObjectType } = useLocalSearchParams<{
    id: string;
    menuObjectType: MenuObjectTypes;
  }>();

  useRumPageView('product-wizard', 'Product Wizard');
  useMenuScroll();

  const { data, loading: menuObjectLoading, error: menuObjectError } = useGetMenuObjectQuery({
    sanityId: id,
    menuObjectType,
  });

  const { isOffer, setIsOffer, filterForAvailability } = useMenuContext();
  const menuObjectData = filterForAvailability(data as SanityMenuObject) as MenuObject | null;

  useLogMenuContent({
    loggedValues: {
      id: menuObjectData?._id || '',
      name: menuObjectData?.name?.locale || '',
      menuType: menuObjectData?._type || '',
    },
    pathname,
  });

  const handleMenuItemInOffer = useHandleMenuItemInOffer(setIsOffer);

  React.useEffect(() => {
    if (!!data && SHOULD_CHECK_ITEM_IN_OFFER) {
      handleMenuItemInOffer(data);
    }
  }, [handleMenuItemInOffer, data]);

  const pageTitle = `${menuObjectData?.name?.locale} - Burger King`;

  if (menuObjectLoading) {
    return (
      <CardModal alwaysHideHeader headerText={pageTitle} noDesktopWebMaxWidth>
        <MenuProductSkeleton type={menuObjectType} />
      </CardModal>
    );
  }

  if (!menuObjectData || menuObjectError) {
    return (
      <CardModal alwaysHideHeader headerText={pageTitle}>
        <MenuItemUnavailable isOffer={isOffer} />
      </CardModal>
    );
  }

  return (
    <CardModal
      testID="product-detail"
      alwaysHideHeader
      headerText={pageTitle}
      noDesktopWebMaxWidth
      hasInternalFooter
    >
      <ProductWizardProvider product={menuObjectData as ProductMenuObject}>
        <PageAnnouncer pageTitle={menuObjectData.name.locale} />
        <ProductDetail />
      </ProductWizardProvider>
    </CardModal>
  );
};
